/**
 * Auth bar — drops into the existing header on the right side.
 *
 * Signed out: two ghost buttons ("Sign in", "Sign up") that open the
 *   AuthModal pre-flipped to the chosen tab.
 * Signed in:  email chip + tier badge + quota remaining + logout.
 */
import type { AuthUser, Quota } from '../hooks/useAuth';
import { BillingButton } from './BillingButton';

interface AuthBarProps {
  user: AuthUser | null;
  quota: Quota | null;
  loading: boolean;
  onOpenAuth: (tab: 'signup' | 'login') => void;
  onLogout: () => void | Promise<void>;
}

export function AuthBar({ user, quota, loading, onOpenAuth, onLogout }: AuthBarProps) {
  if (loading) {
    return <span className="auth-bar__loading">…</span>;
  }

  if (!user) {
    const remaining =
      quota && quota.limit !== null && quota.remaining !== null
        ? `${quota.remaining}/${quota.limit} today`
        : null;
    return (
      <div className="auth-bar">
        {remaining && <span className="auth-bar__quota auth-bar__quota--anon">{remaining}</span>}
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => onOpenAuth('login')}
        >
          Sign in
        </button>
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => onOpenAuth('signup')}
        >
          Sign up
        </button>
      </div>
    );
  }

  const isUnlimited = quota?.limit === null;
  const quotaLabel = isUnlimited
    ? 'Unlimited'
    : quota && quota.remaining !== null && quota.limit !== null
      ? `${quota.remaining}/${quota.limit} today`
      : '';

  return (
    <div className="auth-bar">
      <span
        className={`auth-bar__tier auth-bar__tier--${
          user.lifetimeFree && user.role === 'GUEST' ? 'lifetime' : user.role.toLowerCase()
        }`}
      >
        {user.lifetimeFree && user.role === 'GUEST'
          ? 'Lifetime'
          : user.role === 'SUBSCRIBER'
            ? 'Subscriber'
            : user.role === 'ADMIN'
              ? 'Admin'
              : 'Free'}
      </span>
      {quotaLabel && <span className="auth-bar__quota">{quotaLabel}</span>}
      {/*
        Email intentionally NOT displayed in the header — keeps the UI
        privacy-safe during screen-shares / screenshots. Same convention
        as SMN's header (per memory: "Header: Plugin link + drop email").
      */}
      <BillingButton user={user} />
      {user.role === 'ADMIN' && (
        <a
          href="/admin"
          className="button button--ghost button--small"
          onClick={(e) => {
            // SPA-style nav — let the App branch handle without a real reload.
            e.preventDefault();
            window.history.pushState({}, '', '/admin');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        >
          Admin
        </a>
      )}
      <button
        type="button"
        className="button button--ghost button--small"
        onClick={() => void onLogout()}
        title={`Signed in as ${user.email}`}
      >
        Sign out
      </button>
    </div>
  );
}
